import { useState } from 'react';
import { Check, Pencil, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { ReviseDialog } from './ReviseDialog';
import type { AgentId } from '@/hooks/useJobProgress';
import { useAuth } from '@/hooks/useAuth';
import { postCheckpointDecide } from '@/lib/api';

interface CheckpointActionsProps {
  jobId: string;
  outputId: string;
  agent: AgentId;
  itemKey?: string | null;
  /** Disabled when the row is no longer awaiting_user. */
  disabled?: boolean;
  /** Optional click handler for the Edit button. If absent, edit is hidden. */
  onEdit?: () => void;
  /** Called after a successful decide() so the parent can refresh / scroll. */
  onDone?: (decision: 'approve' | 'revise') => void;
}

export function CheckpointActions({
  jobId,
  outputId,
  agent,
  itemKey,
  disabled,
  onEdit,
  onDone,
}: CheckpointActionsProps) {
  const { token } = useAuth();
  const { toast } = useToast();
  const [reviseOpen, setReviseOpen] = useState(false);
  const [busy, setBusy] = useState<'approve' | 'revise' | null>(null);

  const handleApprove = async () => {
    if (!token) return;
    setBusy('approve');
    try {
      await postCheckpointDecide(token, {
        job_id: jobId,
        output_id: outputId,
        decision: 'approve',
      });
      toast({ title: 'Approved.' });
      onDone?.('approve');
    } catch (err) {
      toast({
        title: 'Approve failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setBusy(null);
    }
  };

  const handleRevise = async (feedback: string) => {
    if (!token) return;
    setBusy('revise');
    try {
      await postCheckpointDecide(token, {
        job_id: jobId,
        output_id: outputId,
        decision: 'revise',
        revise_comment: feedback,
      });
      toast({ title: 'Revision requested.' });
      onDone?.('revise');
    } catch (err) {
      toast({
        title: 'Revise failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
      throw err;
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="flex items-center justify-end gap-3 border-t border-ink/10 pt-4">
        {onEdit ? (
          <Button variant="ghost" onClick={onEdit} disabled={disabled || busy !== null}>
            <Pencil className="mr-2 h-3.5 w-3.5" aria-hidden /> Edit
          </Button>
        ) : null}
        <Button
          variant="ghost"
          onClick={() => setReviseOpen(true)}
          disabled={disabled || busy !== null}
        >
          <RotateCcw className="mr-2 h-3.5 w-3.5" aria-hidden /> Ask to revise
        </Button>
        <Button
          variant="accent"
          onClick={handleApprove}
          disabled={disabled || busy !== null}
        >
          <Check className="mr-2 h-3.5 w-3.5" aria-hidden />
          {busy === 'approve' ? 'Approving…' : 'Approve'}
        </Button>
      </div>

      <ReviseDialog
        open={reviseOpen}
        agent={agent}
        itemKey={itemKey}
        onClose={() => setReviseOpen(false)}
        onSubmit={handleRevise}
      />
    </>
  );
}
